import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarRange, X } from 'lucide-react';
import { unboundedPeriod, type FinancePeriod } from '@forq/finance-data';

/**
 * The evaluation period lives in the URL (`?von=…&bis=…`) rather than component state: it stays
 * put when switching between Cockpit, Auswertungen and Buchhaltung, and a filtered view can be
 * shared or bookmarked — the same deep-link approach as /finance/rechnungen/:nummer.
 */
export function useFinancePeriod(): readonly [FinancePeriod, (patch: Partial<FinancePeriod>) => void] {
  const [params, setParams] = useSearchParams();
  const period = useMemo<FinancePeriod>(
    () => ({ from: params.get('von') ?? '', to: params.get('bis') ?? '' }),
    [params],
  );
  /** Takes a PATCH, so a caller changing one bound cannot revert the other by spreading a stale period. */
  const setPeriod = useCallback((patch: Partial<FinancePeriod>) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      if (patch.from !== undefined) {
        if (patch.from) updated.set('von', patch.from); else updated.delete('von');
      }
      if (patch.to !== undefined) {
        if (patch.to) updated.set('bis', patch.to); else updated.delete('bis');
      }
      return updated;
    }, { replace: true });
  }, [setParams]);
  return [period, setPeriod] as const;
}

interface PeriodFilterProps {
  readonly period: FinancePeriod;
  readonly onChange: (patch: Partial<FinancePeriod>) => void;
}

/** Von/bis window for every period-aware Finance area (Cockpit, Auswertungen, Buchhaltung). */
export function PeriodFilter({ period, onChange }: PeriodFilterProps) {
  const active = Boolean(period.from || period.to);
  return (
    <div className="finance-period">
      <CalendarRange size={14} aria-hidden />
      <span>Zeitraum</span>
      <input
        type="date"
        aria-label="Zeitraum von"
        value={period.from}
        max={period.to || undefined}
        onChange={(event) => onChange({ from: event.target.value })}
      />
      <span>bis</span>
      <input
        type="date"
        aria-label="Zeitraum bis"
        value={period.to}
        min={period.from || undefined}
        onChange={(event) => onChange({ to: event.target.value })}
      />
      {active && (
        <button type="button" className="finance-period__clear" onClick={() => onChange(unboundedPeriod)} aria-label="Zeitraum zurücksetzen">
          <X size={13} />
        </button>
      )}
    </div>
  );
}
